// ===================================================
// GAM Command Center — Slash Command Definitions V4
// 4 block families (A–K taxonomy), 27 commands
// ===================================================
//
// Until 21.09 this file carried its own six-category grouping — בסיסי,
// רשימות, מתקפל, מדיה, מתקדם, שדות — invented here and matching nothing
// else in the system. The vTool Builder's Add-Block menu was already
// grouped by the eleven-family A–K taxonomy in src/lib/blocks/types.ts,
// so the same plugin appeared under one name in one menu and another
// name in the other. Gal's decision, 21.09.2026: one grouping, the
// families.
//
// The letter prefix is the whole point of the slugs — `sort()` on
// "A-content" … "K-embed" is the menu order, so a family added later
// lands in its taxonomy position without anyone choosing a number. The
// four families below are the ones the editor actually has plugins for;
// an empty family is not rendered, because a header with nothing under
// it is a promise the menu cannot keep.
//
// Why each plugin sits where it does — measured, not assigned:
//
//   A-content   README.md: "Static — text, divider, callout, banner. No
//               schema, no data." Headings, lists, callouts, code, quote
//               and divider are exactly that. The native TipTap table is
//               here too and NOT in B-data-view, because
//               editor-block-catalog.ts says so in as many words:
//               "Tables that are vTool Data Views live as
//               'DataView:table' Blocks (separate concern)." A 3×3 grid
//               with no data source filed under תצוגות נתונים would be a
//               false promise.
//
//   C-field     editor-block-catalog.ts maps imageBlock → Field:Image
//               and fileBlock → Field:File. The five form fields already
//               dispatch a fieldType, and Object Reference is the
//               relational-family `relation` type in inline form.
//
//   I-container editor-block-catalog.ts: `details` conformsTo Toggle,
//               "Container-type Block (Category I-container)".
//
//   K-embed     editor-block-catalog.ts: embedBlock conformsTo
//               Embed:External.
//
// This package is deliberately app-independent — zero `@/` imports, so
// it can ship to a second product — and therefore cannot import
// BLOCK_CATEGORY from the app. The slugs are repeated as literals here
// and held to the app's definition by a drift test that lives on the app
// side, where both halves are importable:
// src/lib/blocks/__tests__/slash-family-drift.test.ts

import type { SlashCommandItem } from './types';

/**
 * The A–K block families this editor has plugins for.
 *
 * Source of truth: `BLOCK_CATEGORY` in src/lib/blocks/types.ts. Adding a
 * slug here that does not exist there fails the drift test.
 */
export type SlashFamilySlug = 'A-content' | 'C-field' | 'I-container' | 'K-embed';

export interface SlashCommandCategory {
  /**
   * Stable English key. Used by slash-order.ts as the localStorage key for
   * Gal's custom family order, and as the React key in both menus — so it
   * is a name, not a label, and renaming one forgets that family's saved
   * position (by design: the stored order is a hint, never the truth).
   */
  name: string;
  nameHe: string;
  /** The A–K family this group *is*. Drives menu order via its letter. */
  family: SlashFamilySlug;
  items: SlashCommandItem[];
}

export const slashCommandCategories: SlashCommandCategory[] = [
  {
    name: 'Content',
    nameHe: 'תוכן',
    family: 'A-content',
    items: [
      {
        title: 'Paragraph',
        titleHe: 'טקסט',
        description: 'טקסט רגיל',
        icon: '📝',
        aliases: ['text', 'טקסט', 'פסקה'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setParagraph().run();
        },
      },
      {
        title: 'Heading 1',
        titleHe: 'כותרת 1',
        description: 'כותרת ראשית',
        icon: '𝗛₁',
        aliases: ['h1', 'כותרת', 'כותרת1'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setHeading({ level: 1 }).run();
        },
      },
      {
        title: 'Heading 2',
        titleHe: 'כותרת 2',
        description: 'כותרת משנית',
        icon: '𝗛₂',
        aliases: ['h2', 'כותרת2'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setHeading({ level: 2 }).run();
        },
      },
      {
        title: 'Heading 3',
        titleHe: 'כותרת 3',
        description: 'כותרת קטנה',
        icon: '𝗛₃',
        aliases: ['h3', 'כותרת3'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setHeading({ level: 3 }).run();
        },
      },
      {
        title: 'Bullet List',
        titleHe: 'רשימה',
        description: 'רשימת נקודות',
        icon: '•',
        aliases: ['bullet', 'ul', 'רשימה', 'נקודות'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleBulletList().run();
        },
      },
      {
        title: 'Numbered List',
        titleHe: 'רשימה ממוספרת',
        description: 'רשימה עם מספרים',
        icon: '1.',
        aliases: ['numbered', 'ol', 'מספרים', 'ממוספרת'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleOrderedList().run();
        },
      },
      {
        title: 'Checklist',
        titleHe: 'משימות',
        description: 'רשימת משימות עם צ׳קבוקס',
        icon: '☑️',
        aliases: ['task', 'todo', 'check', 'משימה', 'משימות'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).toggleTaskList().run();
        },
      },
      {
        title: 'Table',
        titleHe: 'טבלה',
        description: 'טבלה עם עמודות ושורות',
        icon: '▦',
        aliases: ['table', 'טבלה', 'grid'],
        command: ({ editor, range }) => {
          editor
            .chain()
            .focus()
            .deleteRange(range)
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
            .run();
        },
      },
      {
        title: 'Callout — Info',
        titleHe: 'הודעה',
        description: 'בלוק מידע צבעוני',
        icon: 'ℹ️',
        aliases: ['callout', 'info', 'הודעה', 'מידע'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setCallout({ variant: 'info' }).run();
        },
      },
      {
        title: 'Callout — Warning',
        titleHe: 'אזהרה',
        description: 'בלוק אזהרה צבעוני',
        icon: '⚠️',
        aliases: ['warning', 'אזהרה', 'זהירות'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setCallout({ variant: 'warning', icon: '⚠️' }).run();
        },
      },
      {
        title: 'Callout — Tip',
        titleHe: 'טיפ',
        description: 'בלוק טיפ שימושי',
        icon: '💡',
        aliases: ['tip', 'טיפ', 'רמז'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setCallout({ variant: 'tip', icon: '💡' }).run();
        },
      },
      {
        title: 'Code Block',
        titleHe: 'קוד',
        description: 'בלוק קוד',
        icon: '{ }',
        aliases: ['code', 'קוד', 'codeblock'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setCodeBlock().run();
        },
      },
      {
        title: 'Quote',
        titleHe: 'ציטוט',
        description: 'בלוק ציטוט',
        icon: '❝',
        aliases: ['quote', 'blockquote', 'ציטוט'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setBlockquote().run();
        },
      },
      {
        title: 'Divider',
        titleHe: 'קו הפרדה',
        description: 'קו הפרדה אופקי',
        icon: '—',
        aliases: ['hr', 'divider', 'line', 'קו'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setHorizontalRule().run();
        },
      },
    ],
  },
  {
    name: 'Field Library',
    nameHe: 'שדות',
    family: 'C-field',
    items: [
      {
        title: 'Text Field',
        titleHe: 'שדה טקסט',
        description: 'שדה טקסט חופשי',
        icon: '📝',
        aliases: ['field', 'text', 'input', 'שדה', 'טקסט'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.dispatchEvent(new CustomEvent('cc-insert-field', { detail: { fieldType: 'short-text' } }));
        },
      },
      {
        title: 'Checkbox Form Field',
        titleHe: "שדה צ'קבוקס",
        description: 'שדה טופס — תיבת סימון כן/לא',
        icon: '✓',
        aliases: ['checkbox field', 'form checkbox', "שדה צ'קבוקס", 'סימון שדה'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.dispatchEvent(new CustomEvent('cc-insert-field', { detail: { fieldType: 'checkbox' } }));
        },
      },
      {
        title: 'Dropdown Field',
        titleHe: 'רשימה נפתחת',
        description: 'בחירה מרשימת אפשרויות',
        icon: '📋',
        aliases: ['dropdown', 'select', 'רשימה', 'נפתחת'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.dispatchEvent(new CustomEvent('cc-insert-field', { detail: { fieldType: 'dropdown' } }));
        },
      },
      {
        title: 'Date Field',
        titleHe: 'שדה תאריך',
        description: 'בורר תאריך',
        icon: '📅',
        aliases: ['date', 'calendar', 'תאריך', 'לוח שנה'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.dispatchEvent(new CustomEvent('cc-insert-field', { detail: { fieldType: 'date' } }));
        },
      },
      {
        title: 'Tags Field',
        titleHe: 'תגיות',
        description: 'תגיות עם צבעים',
        icon: '🏷️',
        aliases: ['tags', 'תגיות', 'labels'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          window.dispatchEvent(new CustomEvent('cc-insert-field', { detail: { fieldType: 'tags' } }));
        },
      },
      {
        title: 'Image',
        titleHe: 'תמונה',
        description: 'הוסף תמונה מ-URL',
        icon: '🖼️',
        aliases: ['image', 'img', 'תמונה', 'תמונות', 'מדיה'],
        command: ({ editor, range }) => {
          const url = window.prompt('הכנס URL של תמונה:');
          if (!url) return;
          editor.chain().focus().deleteRange(range).setImageBlock({ src: url, alt: '' }).run();
        },
      },
      {
        title: 'File',
        titleHe: 'קובץ',
        description: 'צרף קובץ (URL)',
        icon: '📎',
        aliases: ['file', 'attachment', 'קובץ', 'צרף', 'מדיה'],
        command: ({ editor, range }) => {
          const url = window.prompt('הכנס URL של קובץ:');
          if (!url) return;
          const filename = url.split('/').pop() || 'קובץ';
          editor.chain().focus().deleteRange(range).setFileBlock({ url, filename }).run();
        },
      },
      {
        title: 'Object Reference',
        titleHe: 'קישור לישות',
        description: 'קשר למסמך, לקוח, משימה או ישות אחרת',
        icon: '🔗',
        aliases: ['ref', 'entity', 'mention', 'ישות', 'קישור'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          editor.storage.objectRef?.onRequestInsert?.();
        },
      },
      {
        // Bridges through editor.storage.textToRecord (see
        // extensions/TextToRecord.ts) — the same pattern Object Reference
        // uses, since this item only gets {editor, range}, no host closures.
        // The current block's text becomes a real vTask record in the
        // "משימות" tab; the block itself is left untouched (only the trigger
        // text is removed, same as every other slash command).
        //
        // Renamed + de-aliased 22.09 (Gal, text-to-task questionnaire round
        // 2, Q2/Q4): "/משימה" now belongs to the native Checklist item above
        // (A-content family, toggleTaskList — a simple in-document
        // checkbox). This item creates a real vTask record instead, so it
        // must not compete for the same search term — kept reachable via
        // its own name + the bubble-menu "הפוך למשימה" button
        // (FloatingToolbar.tsx), not via "/משימה".
        title: 'Create Task Record',
        titleHe: 'הפוך למשימה (vTask)',
        description: 'צור רשומת משימה אמיתית מהבלוק הזה',
        icon: '🗂️',
        aliases: ['vtask', 'entity task', 'רשומת משימה', 'ישות משימה'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).run();
          editor.storage.textToRecord?.onCreateTask?.();
        },
      },
    ],
  },
  {
    name: 'Layout / Containers',
    nameHe: 'פריסה ומכולות',
    family: 'I-container',
    items: [
      {
        title: 'Toggle',
        titleHe: 'מתקפל',
        description: 'תוכן מתקפל (accordion)',
        icon: '▸',
        aliases: ['toggle', 'collapse', 'accordion', 'מתקפל'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setToggle().run();
        },
      },
      {
        title: 'Toggle H1',
        titleHe: 'כותרת 1 מתקפלת',
        description: 'כותרת ראשית שמתקפלת',
        icon: '▸𝗛₁',
        aliases: ['toggleh1', 'כותרת מתקפלת', 'כותרת1 מתקפלת'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setToggleHeading({ level: 1 }).run();
        },
      },
      {
        title: 'Toggle H2',
        titleHe: 'כותרת 2 מתקפלת',
        description: 'כותרת משנית שמתקפלת',
        icon: '▸𝗛₂',
        aliases: ['toggleh2', 'כותרת2 מתקפלת'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setToggleHeading({ level: 2 }).run();
        },
      },
      {
        title: 'Toggle H3',
        titleHe: 'כותרת 3 מתקפלת',
        description: 'כותרת קטנה שמתקפלת',
        icon: '▸𝗛₃',
        aliases: ['toggleh3', 'כותרת3 מתקפלת'],
        command: ({ editor, range }) => {
          editor.chain().focus().deleteRange(range).setToggleHeading({ level: 3 }).run();
        },
      },
    ],
  },
  {
    name: 'App Embeds',
    nameHe: 'הטמעות',
    family: 'K-embed',
    items: [
      {
        title: 'Embed',
        titleHe: 'הטמעה',
        description: 'YouTube, Maps, Figma, Loom...',
        icon: '🌐',
        aliases: ['embed', 'iframe', 'youtube', 'הטמעה', 'וידאו', 'מדיה'],
        command: ({ editor, range }) => {
          const url = window.prompt('הכנס URL להטמעה (YouTube, Maps, Figma...):');
          if (!url) return;
          editor.chain().focus().deleteRange(range).setEmbed({ url }).run();
        },
      },
    ],
  },
];

// Flat list for backward compatibility
export const slashCommandItems: SlashCommandItem[] = slashCommandCategories.flatMap(
  (cat) => cat.items
);
